import { MutationTree } from 'vuex';
import { ErrorDetail } from '../../types';
import { AutoLoginApp, AutoLoginAppState } from '../auto_login_app/types';
import { Node } from '../node/types';
import * as types from './mutation-types';

// 自動ログインアプリ定義のエラーをクリアする
const resetChangesAllErrors = (state: AutoLoginAppState): void => {
    const searchState = state.search;
    searchState.error = false;
    searchState.errorCode = undefined;
    searchState.errorMsg = undefined;

    const createState = state.create;
    createState.error = false;
    createState.errorTitle = undefined;
    createState.errorDetails = undefined;

    const showState = state.show;
    showState.error = false;
    showState.errorCode = undefined;
    showState.errorMsg = undefined;

    const updateState = state.update;
    updateState.error = false;
    updateState.errorTitle = undefined;
    updateState.errorDetails = undefined;

    const destroyState = state.destroy;
    destroyState.error = false;
    destroyState.errorTitle = undefined;
    destroyState.errorDetails = undefined;

    const csvState = state.csv;
    csvState.error = false;
    csvState.errorCode = undefined;
    csvState.errorMsg = undefined;

    const paFileDownloadState = state.paFileDownload;
    paFileDownloadState.error = false;
    paFileDownloadState.errorTitle = undefined;
    paFileDownloadState.errorDetails = undefined;
};

export const mutations: MutationTree<AutoLoginAppState> = {
    // 自動ログインアプリ定義一覧系
    [types.SEARCH_START](state) {
        resetChangesAllErrors(state);
        const searchState = state.search;
        searchState.isProcessing = true;
        searchState.isFetchSuccess = false;
    },

    [types.SEARCH_SUCCESS](state, payload: { count: number; auto_login_definitions: Array<AutoLoginApp> }) {
        state.autoLoginAppCount = payload.count;
        state.autoLoginApps = payload.auto_login_definitions;

        const searchState = state.search;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = true;
    },

    [types.SEARCH_FAILURE](state, payload: { code: string; message: string }) {
        const searchState = state.search;
        searchState.errorCode = payload.code;
        searchState.errorMsg = payload.message;
        searchState.error = true;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = false;
    },

    // 生成系
    [types.CLEAR_ERROR_AUTO_LOGIN_APP](state) {
        resetChangesAllErrors(state);
        const createState = state.create;
        createState.isCreated = false;
    },

    [types.CREATE_START](state) {
        resetChangesAllErrors(state);
        const createState = state.create;
        createState.isProcessing = true;
    },

    [types.CREATE_SUCCESS](state) {
        const createState = state.create;
        createState.isProcessing = false;
        createState.isCreated = true;
    },

    [types.CREATE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const createState = state.create;
        createState.errorTitle = payload.errorTitle;
        createState.errorDetails = payload.errorDetails;
        createState.error = true;
        createState.isProcessing = false;
    },

    // 自動ログインアプリ定義詳細情報参照系
    [types.SHOW_START](state) {
        resetChangesAllErrors(state);
        const showState = state.show;
        showState.isProcessing = true;
        showState.isFetchSuccess = false;
    },

    [types.SHOW_SUCCESS](state, payload: { auto_login_definitions: AutoLoginApp; nodes: Array<Node> }) {
        const showState = state.show;
        state.autoLoginAppsDetails.autoLoginApp = payload.auto_login_definitions;
        state.autoLoginAppsDetails.nodes = payload.nodes.length > 0 ? payload.nodes : [];
        showState.isProcessing = false;
        showState.isFetchSuccess = true;
    },

    [types.SHOW_FAILURE](state, payload: { code: string; message: string }) {
        const showState = state.show;
        showState.errorCode = payload.code;
        showState.errorMsg = payload.message;
        showState.error = true;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;
    },

    // 自動ログインアプリ定義更新系
    [types.UPDATE_START](state) {
        resetChangesAllErrors(state);
        const updateState = state.update;
        updateState.isProcessing = true;
    },

    [types.UPDATE_SUCCESS](state) {
        const updateState = state.update;
        updateState.isProcessing = false;
    },

    [types.UPDATE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const updateState = state.update;
        updateState.errorTitle = payload.errorTitle;
        updateState.errorDetails = payload.errorDetails;
        updateState.error = true;
        updateState.isProcessing = false;
    },

    // 自動ログインアプリ定義削除系
    [types.DESTROY_START](state) {
        resetChangesAllErrors(state);
        const destroyState = state.destroy;
        destroyState.isProcessing = true;
    },

    [types.DESTROY_SUCCESS](state) {
        const destroyState = state.destroy;
        destroyState.isProcessing = false;
    },

    [types.DESTROY_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const destroyState = state.destroy;
        destroyState.errorTitle = payload.errorTitle;
        destroyState.errorDetails = payload.errorDetails;
        destroyState.error = true;
        destroyState.isProcessing = false;
    },

    // CSV出力系
    [types.CSV_OUTPUT_START](state) {
        resetChangesAllErrors(state);
        const csvState = state.csv;
        csvState.isProcessing = true;
    },

    [types.CSV_OUTPUT_SUCCESS](state) {
        const csvState = state.csv;
        csvState.isProcessing = false;
    },

    [types.CSV_OUTPUT_FAILURE](state, payload: { code: string; message: string }) {
        const csvState = state.csv;
        csvState.errorCode = payload.code;
        csvState.errorMsg = payload.message;
        csvState.error = true;
        csvState.isProcessing = false;
    },

    // PA定義ファイルダウンロード系
    [types.PA_FILE_DOWNLOAD_START](state) {
        resetChangesAllErrors(state);
        const paFileDownloadState = state.paFileDownload;
        paFileDownloadState.isProcessing = true;
    },

    [types.PA_FILE_DOWNLOAD_SUCCESS](state) {
        const paFileDownloadState = state.paFileDownload;
        paFileDownloadState.isProcessing = false;
    },

    [types.PA_FILE_DOWNLOAD_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const paFileDownloadState = state.paFileDownload;
        paFileDownloadState.errorTitle = payload.errorTitle;
        paFileDownloadState.errorDetails = payload.errorDetails;
        paFileDownloadState.error = true;
        paFileDownloadState.isProcessing = false;
    },

    // FATALエラー系
    [types.FATAL_FAILURE](state) {
        const searchState = state.search;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = false;

        const createState = state.create;
        createState.isProcessing = false;
        createState.isCreated = false;

        const showState = state.show;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;

        const updateState = state.update;
        updateState.isProcessing = false;

        const destroyState = state.destroy;
        destroyState.isProcessing = false;

        const csvState = state.csv;
        csvState.isProcessing = false;

        const paFileDownloadState = state.paFileDownload;
        paFileDownloadState.isProcessing = false;
    },

    [types.CLEAR_ALL](state) {
        resetChangesAllErrors(state);
        state.autoLoginApps = undefined;
        state.autoLoginAppsDetails.autoLoginApp = undefined;
        state.autoLoginAppsDetails.nodes = undefined;
        state.autoLoginAppCount = 0;
    },
};
